import React, { FC, useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Alert,
  InputAdornment,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop'
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'
import FilterListIcon from '@mui/icons-material/FilterList'
import FunctionsIcon from '@mui/icons-material/Functions'
import DeleteIcon from '@mui/icons-material/Delete'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Tree from '../tree/Tree'
import BaseSelectionModal from './BaseSelectionModal'
import { AxisEditorProps } from './types'
import { GenNode } from '../../types/carbon/models/GenNode'
import { getNodeID, getNodeTitle } from '../../utils/nodeTypes'
import {
  addBasesToCodeframe,
  copyNodeInTree,
  getExistingBaseTypes,
  removeAxisNode,
  removeBasesFromCodeframe,
  reorderAxisNode,
} from '../../utils/axisHelpers'

const AxisEditor: FC<AxisEditorProps> = ({
  title,
  nodes,
  onChange,
  syntax,
  onSyntaxApply,
  onAddSelected,
  onOpenFilterModal,
  hasSelectedVariables = false,
  error = null
}) => {
  const [expanded, setExpanded] = useState<string[]>([])
  const [baseModalOpen, setBaseModalOpen] = useState(false)
  const [currentEditNode, setCurrentEditNode] = useState<GenNode | null>(null)
  const [initialBaseTypes, setInitialBaseTypes] = useState<string[]>([])
  const [menuAnchors, setMenuAnchors] = useState<Record<string, HTMLElement>>({})
  const [currentMenuNode, setCurrentMenuNode] = useState<GenNode | null>(null)
  const [selectedId, setSelectedId] = useState<string>('')
  const [moveAnchorEl, setMoveAnchorEl] = useState<HTMLElement | null>(null)
  const [isMoveMenuOpen, setIsMoveMenuOpen] = useState<boolean>(false)
  const [isEditingSyntax, setIsEditingSyntax] = useState(false)
  const [editedSyntax, setEditedSyntax] = useState('')
  const [isApplyingSyntax, setIsApplyingSyntax] = useState(false)

  const handleNodeExpandToggle = useCallback((id: string, node: GenNode, isExpanded: boolean) => {
    setExpanded(prev => {
      if (isExpanded) {
        return [...prev, id]
      } else {
        return prev.filter(id => id !== id)
      }
    })
  }, [])

  // Create the node actions dropdown for each tree node
  const getNodeActions = useCallback((node: GenNode) => {
    const nodeId = getNodeID(node)
    // Selection highlight when opening menu
    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchors(prev => ({ ...prev, [nodeId]: event.currentTarget }))
      setCurrentMenuNode(node)
      setSelectedId(nodeId)
      event.stopPropagation()
    }
    
    return (
      <>
        <IconButton
          size="small"
          onClick={handleOpenMenu}
          aria-label={`More actions for ${getNodeTitle(node)}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </>
    )
  }, [nodes])

  const handleMenuClose = useCallback(() => {
    // Close both the root context menu and any open "Move" submenu
    setMenuAnchors({})
    setCurrentMenuNode(null)
    setMoveAnchorEl(null)
    setIsMoveMenuOpen(false)
  }, [])

  const handleMoveUp = useCallback(() => {
    if (!currentMenuNode) return
    const updated = reorderAxisNode(nodes, currentMenuNode.id!, 'up')
    onChange(updated)
    handleMenuClose()
  }, [currentMenuNode, nodes, onChange, handleMenuClose])

  const handleMoveDown = useCallback(() => {
    if (!currentMenuNode) return
    const updated = reorderAxisNode(nodes, currentMenuNode.id!, 'down')
    onChange(updated)
    handleMenuClose()
  }, [currentMenuNode, nodes, onChange, handleMenuClose])

  const handleMoveToTop = useCallback(() => {
    if (!currentMenuNode) return
    const updated = reorderAxisNode(nodes, currentMenuNode.id!, 'top')
    onChange(updated)
    handleMenuClose()
  }, [currentMenuNode, nodes, onChange, handleMenuClose])

  const handleMoveToBottom = useCallback(() => {
    if (!currentMenuNode) return
    const updated = reorderAxisNode(nodes, currentMenuNode.id!, 'bottom')
    onChange(updated)
    handleMenuClose()
  }, [currentMenuNode, nodes, onChange, handleMenuClose])

  const handleCopy = useCallback(() => {
    if (!currentMenuNode) return
    const updated = copyNodeInTree(nodes, currentMenuNode)
    onChange(updated)
    handleMenuClose()
  }, [currentMenuNode, nodes, onChange, handleMenuClose])

  const handleRemove = useCallback(() => {
    if (!currentMenuNode) return
    onChange(removeAxisNode(nodes, currentMenuNode.id))
    handleMenuClose()
  }, [currentMenuNode, nodes, onChange, handleMenuClose])

  const handleAddFilter = useCallback(() => {
    if (onOpenFilterModal && currentMenuNode) {
      onOpenFilterModal(currentMenuNode)
    }
    handleMenuClose()
  }, [currentMenuNode, onOpenFilterModal, handleMenuClose])

  const handleAddBases = useCallback(() => {
    if (currentMenuNode) {
      setCurrentEditNode(currentMenuNode)
      // Get existing base types from the codeframe
      const existingBases = getExistingBaseTypes(currentMenuNode)
      setInitialBaseTypes(existingBases)
      setBaseModalOpen(true)
    }
    handleMenuClose()
  }, [currentMenuNode, handleMenuClose])

  const handleEditFilter = useCallback(() => {
    if (onOpenFilterModal && currentMenuNode && currentMenuNode.type === 'Filter') {
      // Pass the existing Filter node to the modal for editing
      onOpenFilterModal(currentMenuNode)
    }
    handleMenuClose()
  }, [currentMenuNode, onOpenFilterModal, handleMenuClose])

  const handleMoveMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMoveAnchorEl(event.currentTarget)
    setIsMoveMenuOpen(true)
    event.stopPropagation()
  }, [])

  // Edit feature removed per request

  // Recursive helper to update a node at any depth in the tree
  const updateNodeRecursively = useCallback((arr: GenNode[], targetId: number, updatedNode: GenNode): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === targetId)
    if (idx !== -1) {
      // Found the target node at this level - replace it
      const copy = [...arr]
      copy[idx] = updatedNode
      return [copy, true]
    }

    // Not found at this level - search children recursively
    let changed = false
    const next = arr.map(n => {
      if (n.children && n.children.length > 0) {
        const [childList, wasFound] = updateNodeRecursively(n.children, targetId, updatedNode)
        if (wasFound) {
          changed = true
          return { ...n, children: childList }
        }
      }
      return n
    })
    return [next, changed]
  }, [])

  const handleSaveBases = useCallback((baseTypesToAdd: string[], baseTypesToRemove: string[]) => {
    if (!currentEditNode) return

    let updatedNode = currentEditNode

    // First, remove bases that should be removed
    if (baseTypesToRemove.length > 0) {
      updatedNode = removeBasesFromCodeframe(updatedNode, baseTypesToRemove)
    }

    // Then, add new bases
    if (baseTypesToAdd.length > 0) {
      updatedNode = addBasesToCodeframe(updatedNode, baseTypesToAdd)
    }

    // Use recursive update to handle both top-level and nested codeframes
    const [updatedNodes] = updateNodeRecursively(nodes, currentEditNode.id!, updatedNode)
    onChange(updatedNodes)
    setCurrentEditNode(null)
    setBaseModalOpen(false)
  }, [currentEditNode, nodes, onChange, updateNodeRecursively])

  // Edit dialog removed; keep a no-op to maintain minimal diff structure
  const handleSaveEdit = useCallback((_updatedNode: GenNode) => {
    return
  }, [])

  useEffect(() => {
    if (!isEditingSyntax) {
      setEditedSyntax(syntax)
    }
  }, [syntax, isEditingSyntax])

  const handleStartSyntaxEdit = useCallback(() => {
    setEditedSyntax(syntax)
    setIsEditingSyntax(true)
  }, [syntax])

  const handleCancelSyntaxEdit = useCallback(() => {
    setIsEditingSyntax(false)
    setEditedSyntax(syntax)
  }, [syntax])

  const handleApplySyntaxEdit = useCallback(async () => {
    if (!onSyntaxApply) {
      setIsEditingSyntax(false)
      return
    }

    setIsApplyingSyntax(true)
    try {
      await Promise.resolve(onSyntaxApply(editedSyntax))
      setIsEditingSyntax(false)
    } catch (error) {
      console.error('Failed to apply syntax string:', error)
    } finally {
      setIsApplyingSyntax(false)
    }
  }, [onSyntaxApply, editedSyntax])


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper' }}>
      <Box sx={{ px: 4, pt: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onAddSelected && (
            <IconButton
              onClick={onAddSelected}
              size="small"
              aria-label={`Add selected to ${title.replace(/ Editor$/, '')}`}
              sx={{
                backgroundColor: '#009900',
                color: '#ffffff',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#007700',
                },
              }}
              disabled={!hasSelectedVariables}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
            {title}
          </Typography>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Syntax"
            value={isEditingSyntax ? editedSyntax : syntax}
            onChange={event => setEditedSyntax(event.target.value)}
            fullWidth
            size="small"
            margin="dense"
            multiline={isEditingSyntax}
            minRows={isEditingSyntax ? 5 : 1}
            maxRows={isEditingSyntax ? 5 : 1}
            placeholder={syntax ? undefined : 'Syntax string will appear here'}
            InputProps={{
              readOnly: !isEditingSyntax,
              sx: {
                fontSize: isEditingSyntax ? '1rem' : '0.8rem',
                color: isEditingSyntax ? 'text.primary' : 'text.secondary',
              },
              endAdornment: !isEditingSyntax ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleStartSyntaxEdit} aria-label={`Edit ${title.replace(/ Editor$/, '')} syntax`} sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            InputLabelProps={{
              sx: {
                fontSize: isEditingSyntax ? '1rem' : '0.8rem',
                color: isEditingSyntax ? 'text.primary' : 'text.secondary',
              },
            }}
          />
        </Box>
        {isEditingSyntax && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancelSyntaxEdit}
              disabled={isApplyingSyntax}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApplySyntaxEdit}
              disabled={isApplyingSyntax}
            >
              Apply
            </Button>
          </Box>
        )}
        
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 4 }}>
        {nodes && nodes.length > 0 ? (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Tree
              nodes={nodes}
              expanded={expanded}
              getNodeActions={getNodeActions}
              onNodeExpandToggle={handleNodeExpandToggle}
              selected={selectedId}
            />
          </Box>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">
              No items added to this axis yet
            </Typography>
          </Box>
        )}
      </Box>

      {/* Actions Menu for each node */}
      {Object.entries(menuAnchors).map(([nodeId, anchorEl]) => {
        const node = currentMenuNode
        // Enable all move options; they will operate within the node's sibling group
        const canMoveUp = true
        const canMoveDown = true
        
        return (
          <Menu
            key={nodeId}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMoveMenuClick}>
              <ListItemIcon>
                <ZoomOutMapIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Move</ListItemText>
              <ChevronRightIcon fontSize="small" sx={{ ml: 'auto' }} />
            </MenuItem>
            <Menu
              anchorEl={moveAnchorEl}
              open={isMoveMenuOpen}
              onClose={() => {
                setMoveAnchorEl(null)
                setIsMoveMenuOpen(false)
              }}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem onClick={handleMoveUp}>
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Up</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMoveDown}>
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Down</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMoveToTop}>
                <ListItemIcon>
                  <VerticalAlignTopIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Top</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMoveToBottom}>
                <ListItemIcon>
                  <VerticalAlignBottomIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Bottom</ListItemText>
              </MenuItem>
            </Menu>
            {(node?.type === 'Codeframe' || node?.type === 'Filter') && (
            <MenuItem onClick={handleAddFilter}>
              <ListItemIcon>
                <FilterListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Filter</ListItemText>
            </MenuItem>
            )}
            {node?.type === 'Filter' && (
              <MenuItem onClick={handleEditFilter}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Filter</ListItemText>
              </MenuItem>
            )}
            {node?.type === 'Codeframe' && (
              <MenuItem onClick={handleAddBases}>
                <ListItemIcon>
                  <FunctionsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add/Remove Bases</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleCopy}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleRemove} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Remove</ListItemText>
            </MenuItem>
          </Menu>
        )
      })}

      {/* Base Selection Modal */}
      <BaseSelectionModal
        open={baseModalOpen}
        onClose={() => setBaseModalOpen(false)}
        onSave={handleSaveBases}
        selectedNode={currentEditNode}
        initialBaseTypes={initialBaseTypes}
      />

      {/* Edit modal removed per request */}
    </Box>
  )
}

export default AxisEditor
